import React, { useRef, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useField } from 'formik';
import { Q1Value } from 'models/documents';
import { DataGridComplete, DataGridRow } from 'components/DataGrid';
import Dropdown from 'components/Input/dropdown/Dropdown';
import { BoundingBox } from 'models/response';
import utils from 'utils';

export interface CellDropdown {
  name: string;
  focus: boolean;
  onFocus: (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => void;
  row: DataGridRow;
  code: number;
  options: { code: string; description: string; sign?: -1 | 1 }[];
  gridRef?: any;
  disabled?: boolean;
  key: number;
}

const CellDropdown = ({ name, focus, onFocus, row, code, options, gridRef, disabled, key }: CellDropdown) => {
  const [field, , helpers] = useField<Q1Value>(name);
  const selectRef = useRef<HTMLElement | null>(null);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const [inputValue, setInputValue] = useState<string>('');

  // Formatting and updating the rows state on inputs change
  const onFieldChange = (value: string, rowId: string, fieldId: string | number) => {
    const body: DataGridComplete[] | undefined = field.value.body?.map((elem: DataGridComplete) => {
      if (elem.id === rowId) {
        return {
          ...elem,
          [fieldId]: {
            content: value,
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
        };
      }
      return elem;
    });
    if (body) helpers.setValue({ ...field.value, body });
  };

  const getLabel = (optionType: string, option: { code: any; description: any }) => {
    switch (optionType) {
      case 'code-description':
        return `${option.code} - ${option.description}`;
      case 'code':
        return option.code;
      case 'description':
      default:
        return option.description;
    }
  };

  const optionType = utils.input.getLabelTypeByKey(key);

  let value: string | { label: string; value: string } = '';
  const fieldValue = field.value.body?.find(({ id }) => id === row.id);
  if (fieldValue) {
    const valueCode = fieldValue[code]?.content;
    const option = options?.find((e) => valueCode === e.code);

    if (option) {
      value = { label: getLabel(optionType, option), value: option.code };
    }
  }

  useUpdateEffect(() => {
    if (focus) {
      selectRef.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (focus && !inputValue) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((isMac && event.metaKey && event.key === 'c') || (!isMac && event.ctrlKey && event.key === 'c')) {
          navigator.clipboard.writeText(typeof value === 'object' ? value.label : '');
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [focus, value, isMac, inputValue]);

  return (
    <Dropdown
      id="cell-dropdown"
      onInputChange={(val) => setInputValue(val)}
      isClearable={focus}
      setRef={selectRef}
      value={value}
      options={utils.input.buildOptions(
        options || [],
        utils.input.getLabelTypeByKey(key).includes('description') ? 'description' : 'code',
        'code',
        utils.input.getLabelTypeByKey(key) === 'code-description',
      )}
      onChange={(option) => onFieldChange(option?.value, row.id, code)}
      onFocus={() => onFocus(fieldValue ? fieldValue[code] : null)}
      menuIsOpen={focus && !disabled}
      noBorder
      isPortal
      minWidthLabel="auto"
      closeMenuOnScroll={(event) => {
        if (event.target instanceof HTMLElement) {
          if (
            event.target?.className?.includes('MenuList') ||
            event.target?.className?.includes('ResizableTwoRowsLayout') ||
            document.querySelector('.react-select > div > div')?.className === event.target?.className ||
            event.target?.className?.includes('cell-text-input') ||
            event.target.parentElement?.className?.includes('menu') ||
            event.target.parentElement?.className?.includes('BodyContainer')
          ) {
            return false;
          }
          if (gridRef?.current) {
            gridRef.current.focusCell(null);
          }
          return true;
        }

        return true;
      }}
      disabled={disabled}
    />
  );
};

export default CellDropdown;
